import {
  type AnimationType,
  type AnimationEasing,
  type HoverEffectType,
  HOVER_INTENSITY_DEFAULTS,
} from '../types/animation';
import type { CSSObject } from '@emotion/serialize';
import type { RenderContextType } from '../context/RenderContext';

interface AnimationProps {
  animationType?: AnimationType;
  animationDuration?: number;
  animationDelay?: number;
  animationEasing?: AnimationEasing;
}

export const getAnimationDataAttributes = (
  props: AnimationProps,
): Record<string, string | undefined> => {
  const { animationType, animationDuration, animationDelay, animationEasing } = props;

  if (!animationType || animationType === 'none') {
    return {};
  }

  return {
    'data-animation': animationType,
    'data-animation-duration': String(animationDuration ?? 600),
    'data-animation-delay': String(animationDelay ?? 0),
    'data-animation-easing': animationEasing ?? 'ease-out',
  };
};

export const getAnimationInitialClass = (
  animationType: AnimationType | undefined,
  environment: RenderContextType['environment'],
): string => {
  if (!animationType || animationType === 'none') {
    return '';
  }

  if (environment === 'builder') {
    return '';
  }

  return 'b4-anim-pending';
};

export const getHoverStyles = (
  hoverEffect: HoverEffectType | undefined,
  duration: number = 300,
  intensity?: number,
  hasExistingBoxShadow?: boolean,
): CSSObject => {
  if (!hoverEffect || hoverEffect === 'none') {
    return {};
  }

  const i = intensity ?? HOVER_INTENSITY_DEFAULTS[hoverEffect];

  const liftStyle: CSSObject = {
    transform: `translateY(-${i}px)`,
    ...(hasExistingBoxShadow ? {} : { boxShadow: `0 ${i * 2}px ${i * 6}px rgba(0,0,0,0.15)` }),
  };

  const hoverMap: Record<string, CSSObject> = {
    scale: { transform: `scale(${i / 100})` },
    lift: liftStyle,
    glow: { boxShadow: `0 0 ${i}px rgba(59,130,246,0.5)` },
    brighten: { filter: `brightness(${i / 100})` },
  };

  const hoverRule = hoverMap[hoverEffect];
  if (!hoverRule || Object.keys(hoverRule).length === 0) {
    return {};
  }

  return {
    transition: `transform ${duration}ms ease, box-shadow ${duration}ms ease, filter ${duration}ms ease`,
    '&:hover': hoverRule,
  } as CSSObject;
};

export const getStaggerDataAttribute = (
  staggerDelay: number | undefined,
): Record<string, string> => {
  if (!staggerDelay || staggerDelay <= 0) {
    return {};
  }

  return {
    'data-stagger-delay': String(staggerDelay),
  };
};
